"use client";

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  BuildingStorefrontIcon,
  CubeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowPathIcon,
} from "@heroicons/react/20/solid";
import AlertDialog from "./AlertDialog";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

// ─── Inline search utilities (mirrors searchUtils.js) ────────────────────────
function normaliseStr(str) {
  if (!str) return "";
  return String(str)
    .toLowerCase()
    .replace(/[''"""]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function buildSearchBlob(item) {
  const parts = [
    item.itemCode,
    item.description,
    item.sectionNumber || item.costCode?.code || "",
    item.uom,
    item.size,
    item.manufacturer,
    item.partNumber,
    item.itemNotes,
    item.defaultVendor?.companyName || "",
    ...(item.vendorPrices || []).map((vp) => vp.vendor?.companyName || ""),
    ...(item.vendorPrices || []).map((vp) => vp.partNumber || ""),
  ];
  return normaliseStr(parts.join("  "));
}

function tokenVariants(token) {
  const variants = [token];
  // "358" → "3-5/8", "3 5/8", "35/8"
  const fractionCondensed = token.match(/^(\d+)(\d)(\d)$/);
  if (fractionCondensed) {
    const [, a, b, c] = fractionCondensed;
    variants.push(`${a}-${b}/${c}`, `${a} ${b}/${c}`, `${a}${b}/${c}`);
  }
  // "3-5/8" → "358"
  const fractionExpanded = token.match(/^(\d+)[\s-]?(\d)\/(\d)$/);
  if (fractionExpanded) {
    const [, a, b, c] = fractionExpanded;
    variants.push(`${a}${b}${c}`);
  }
  // "20ga" / "20gauge"
  const gaugeMatch = token.match(/^(\d+)(ga|gauge)$/i);
  if (gaugeMatch) {
    const [, num] = gaugeMatch;
    variants.push(`${num}ga`, `${num} ga`, `${num}gauge`);
  }
  return variants;
}

function multiTokenSearch(items, searchStr) {
  const raw = normaliseStr(searchStr);
  if (!raw) return items;
  const tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return items;
  return items.filter((item) => {
    if (!item._searchBlob) item._searchBlob = buildSearchBlob(item);
    return tokens.every((token) =>
      tokenVariants(token).some((v) => item._searchBlob.includes(v)),
    );
  });
}

function clearSearchCache(items) {
  items.forEach((item) => delete item._searchBlob);
}

// ─── Coverage parser ─────────────────────────────────────────────────────────
// Parses a user-entered size string and returns the numeric coverage in the
// item's UOM, or null if it cannot be determined.
//
//  SF items:  "4'x8'"  "4x8"  "4 x 8"  "48x96""  → width_ft × height_ft
//  LF items:  "8'"  "10'"  "12"  "96""              → length in feet
//
// Converts a measurement token to decimal feet.
// Handles: 8  8'  8.5'  8'6"  8'6  96"  8' 6"  8'3/4"  5'5"  4.5'
function tokenToFeet(tok) {
  if (!tok) return null;
  const t = tok.trim();

  // Pure inches: 96"
  const pureInches = t.match(/^([\d.]+)"$/);
  if (pureInches) return parseFloat(pureInches[1]) / 12;

  // Feet + fraction inches: 12'3/4"  →  feet + (num/den)/12
  const ftFracIn = t.match(/^([\d.]+)'\s*(\d+)\/(\d+)"?$/);
  if (ftFracIn) {
    const ft = parseFloat(ftFracIn[1]);
    const frac = parseInt(ftFracIn[2]) / parseInt(ftFracIn[3]);
    return ft + frac / 12;
  }

  // Feet + whole/decimal inches: 8'6"  8'6  5'5"
  const ftIn = t.match(/^([\d.]+)'\s*([\d.]+)"?$/);
  if (ftIn) {
    return parseFloat(ftIn[1]) + parseFloat(ftIn[2]) / 12;
  }

  // Plain feet (decimal ok): 4'  4.5'  8  12.75
  const plainFt = t.match(/^([\d.]+)'?$/);
  if (plainFt) return parseFloat(plainFt[1]);

  return null;
}

// Parses a size string and returns numeric coverage in the item's UOM.
// Returns null if the size cannot be parsed.
//
// SF / SY / SHEET:  WxH in any combination of the above formats
//   "4'x8'"  "4.5'x5'"  "48"x96""  "4x8"
// LF:  single length in any of the above formats
//   "8'"  "5'5""  "96""  "12'3/4""  "145""  "8.5"
function parseCoverage(size, uom) {
  if (!size || !uom) return null;
  const s = size.trim();
  const u = uom.toUpperCase();

  if (u === "SF" || u === "SY" || u === "SHEET") {
    // Split on x / × — but only the LAST occurrence of x that separates two
    // measurement tokens, so "4'x8'" splits cleanly even if a token has no x.
    const xIdx = s.search(/[x×]/i);
    if (xIdx === -1) return null;
    const left = s.slice(0, xIdx);
    const right = s.slice(xIdx + 1);
    const w = tokenToFeet(left);
    const h = tokenToFeet(right);
    if (w == null || h == null || w <= 0 || h <= 0) return null;
    const sf = parseFloat((w * h).toFixed(6));
    if (u === "SY") return parseFloat((sf / 9).toFixed(6));
    return sf;
  }

  if (u === "LF") {
    const ft = tokenToFeet(s);
    return ft != null && ft > 0 ? parseFloat(ft.toFixed(6)) : null;
  }

  return null;
}

// ─── Value calculation ───────────────────────────────────────────────────────
// For EA/non-coverage items:  qty × defaultCost
// For SF/LF/coverage items:  qty × coveragePerUnit × defaultCost
//   because defaultCost is stored as $/SF or $/LF (the rate), not $/piece.
//   coveragePerUnit tells us how many SF/LF are in one piece.
function calcItemValue(item) {
  const qty = item.quantity || 0;
  const rate = item.defaultCost || 0;
  if (isCoverageUom(item.uom)) {
    const coverage = parseFloat(item.coveragePerUnit) || 0;
    return qty * coverage * rate;
  }
  return qty * rate;
}

// Cost per piece (what you actually pay per physical sheet/length)
function calcCostPerPiece(item) {
  if (isCoverageUom(item.uom)) {
    const coverage = parseFloat(item.coveragePerUnit) || 0;
    return (item.defaultCost || 0) * coverage;
  }
  return item.defaultCost || 0;
}

// ─── InfoTip ─────────────────────────────────────────────────────────────────
// Inline ⓘ that shows a tooltip on hover. Self-contained, no library needed.
function InfoTip({ text }) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative inline-flex items-center ml-1 align-middle">
      <button
        type="button"
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
        className="text-gray-500 hover:text-gray-300 focus:outline-none leading-none"
        tabIndex={-1}
        aria-label="More information"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-3.5 h-3.5"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {visible && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-[99999] w-64 rounded-lg bg-gray-900 border border-gray-600 px-3 py-2 text-xs text-gray-200 leading-relaxed shadow-xl pointer-events-none">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </span>
      )}
    </span>
  );
}

const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    "&:hover": { borderColor: "#f97316" },
    minHeight: "38px",
    fontSize: "13px",
  }),
  input: (provided) => ({ ...provided, color: "#e5e7eb" }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "13px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "13px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "13px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    zIndex: 9999,
  }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
};

const ADJUSTMENT_TYPES = [
  { value: "add", label: "Add Stock", color: "text-green-400" },
  { value: "remove", label: "Remove Stock", color: "text-red-400" },
  { value: "set", label: "Set Quantity", color: "text-blue-400" },
  { value: "transfer", label: "Transfer", color: "text-purple-400" },
];

const ADJUSTMENT_REASONS = [
  { value: "receiving", label: "Receiving from PO" },
  { value: "stockout", label: "Stock-Out Order" },
  { value: "return", label: "Return from Site" },
  { value: "adjustment", label: "Physical Count Adjustment" },
  { value: "damaged", label: "Damaged / Write-Off" },
  { value: "transfer", label: "Warehouse Transfer" },
  { value: "other", label: "Other" },
];

// Google Places select styles — matches the app dark theme
const placesStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "#374151",
    borderColor: state.isFocused ? "#f97316" : "#4b5563",
    color: "#e5e7eb",
    boxShadow: "none",
    outline: "none",
    "&:hover": { borderColor: "#f97316" },
    fontSize: "13px",
    minHeight: "38px",
  }),
  input: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    outline: "none",
    boxShadow: "none",
    border: "none",
    margin: 0,
    padding: 0,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#e5e7eb",
    fontSize: "13px",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9ca3af",
    fontSize: "13px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
    color: state.isSelected ? "#f97316" : "#e5e7eb",
    "&:hover": { backgroundColor: "#4b5563" },
    fontSize: "13px",
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "#374151",
    borderRadius: "0.375rem",
    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.4)",
    zIndex: 9999,
  }),
};

const EMPTY_ADDRESS = {
  formattedAddress: "",
  street: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  latitude: null,
  longitude: null,
  placeId: "",
};

// ─── Warehouse Modal ──────────────────────────────────────────────────────────
function WarehouseModal({ warehouse, onSave, onClose }) {
  const [form, setForm] = useState(() => {
    if (warehouse) {
      return {
        name: warehouse.name || "",
        code: warehouse.code || "",
        // Support both legacy string address and new structured object
        address:
          warehouse.address && typeof warehouse.address === "object"
            ? warehouse.address
            : { ...EMPTY_ADDRESS, formattedAddress: warehouse.address || "" },
        notes: warehouse.notes || "",
        isActive: warehouse.isActive !== false,
      };
    }
    return {
      name: "",
      code: "",
      address: { ...EMPTY_ADDRESS },
      notes: "",
      isActive: true,
    };
  });

  const [addressError, setAddressError] = useState("");
  const token = localStorage.getItem("token");

  const handleAddressSelect = async (place) => {
    setAddressError("");

    if (!place?.value?.place_id) {
      setAddressError("Please select a valid address from the dropdown.");
      return;
    }

    try {
      const res = await axios.get(
        `${API_BASE}/leads/address/${place.value.place_id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data.success && res.data.address) {
        const a = res.data.address;
        setForm((f) => ({
          ...f,
          address: {
            formattedAddress: a.formattedAddress || "",
            street: a.street || "",
            city: a.city || "",
            state: a.state || "",
            postalCode: a.postalCode || "",
            country: a.country || "",
            latitude: a.latitude || null,
            longitude: a.longitude || null,
            placeId: a.placeId || place.value.place_id,
          },
        }));
      } else {
        // Fallback: store whatever label Google gave us
        setForm((f) => ({
          ...f,
          address: {
            ...EMPTY_ADDRESS,
            formattedAddress: place.label || "",
            placeId: place.value.place_id,
          },
        }));
      }
    } catch (err) {
      console.error("Address fetch error:", err.message);
      // Fallback on API error
      setForm((f) => ({
        ...f,
        address: {
          ...EMPTY_ADDRESS,
          formattedAddress: place.label || "",
          placeId: place.value.place_id,
        },
      }));
    }
  };

  const clearAddress = () => {
    setForm((f) => ({ ...f, address: { ...EMPTY_ADDRESS } }));
    setAddressError("");
  };

  // Build the react-select value object from stored address
  const addressSelectValue = form.address.formattedAddress
    ? {
        label: form.address.formattedAddress,
        value: {
          description: form.address.formattedAddress,
          place_id: form.address.placeId || "",
        },
      }
    : null;

  const handleSave = () => {
    if (!form.name.trim() || !form.code.trim()) return;
    onSave(form);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-lg p-6">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-white">
            {warehouse ? "Edit Warehouse" : "Add Warehouse"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Name + Code */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none"
                placeholder="Main Warehouse"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Code *
              </label>
              <input
                type="text"
                value={form.code}
                onChange={(e) =>
                  setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))
                }
                className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 font-mono focus:border-orange-500 focus:outline-none"
                placeholder="WH-01"
              />
            </div>
          </div>

          {/* Google Places Address */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Address
            </label>
            <GooglePlacesAutocomplete
              apiKey="AIzaSyBnTWkimXgryzzdI3KVPWNQwvtJSrm2pAg"
              autocompletionRequest={{
                componentRestrictions: { country: ["ca", "us"] },
              }}
              selectProps={{
                value: addressSelectValue,
                onChange: handleAddressSelect,
                placeholder: "Search address…",
                isClearable: true,
                onClear: clearAddress,
                styles: placesStyles,
              }}
            />
            {addressError && (
              <p className="mt-1 text-xs text-red-400">{addressError}</p>
            )}

            {/* Structured address fields (read-only confirmation) */}
            {form.address.formattedAddress && (
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5">
                {[
                  { label: "Street", key: "street" },
                  { label: "City", key: "city" },
                  { label: "Province / State", key: "state" },
                  { label: "Postal Code", key: "postalCode" },
                  { label: "Country", key: "country" },
                ].map(({ label, key }) =>
                  form.address[key] ? (
                    <div key={key}>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                        {label}
                      </p>
                      <p className="text-xs text-gray-300">
                        {form.address[key]}
                      </p>
                    </div>
                  ) : null,
                )}
                {form.address.latitude && (
                  <div className="col-span-2">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                      Coordinates
                    </p>
                    <p className="text-xs text-gray-400 font-mono">
                      {form.address.latitude.toFixed(6)},{" "}
                      {form.address.longitude.toFixed(6)}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) =>
                setForm((f) => ({ ...f, notes: e.target.value }))
              }
              className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none"
              rows={2}
              placeholder="Additional details…"
            />
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-400">Active</label>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))}
              className={`relative w-11 h-6 rounded-full transition-colors ${
                form.isActive ? "bg-green-500" : "bg-gray-600"
              }`}
            >
              <span
                className={`absolute w-4 h-4 bg-white rounded-full top-1 transition-transform ${
                  form.isActive ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!form.name.trim() || !form.code.trim()}
            className="px-4 py-2 text-sm text-white bg-orange-500 rounded hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {warehouse ? "Update Warehouse" : "Create Warehouse"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Coverage UOMs that require a size to be specified
const COVERAGE_UOMS = new Set(["SF", "LF", "SY", "SHEET"]);
const isCoverageUom = (uom) => COVERAGE_UOMS.has((uom || "").toUpperCase());

// ─── Add Inventory Item Modal ─────────────────────────────────────────────────
// existingEntries: array of { itemDatabaseId, warehouse, size } for duplicate checks
function AddInventoryItemModal({
  warehouses,
  existingEntries,
  onSave,
  onClose,
}) {
  const [search, setSearch] = useState("");
  // All material items loaded once on mount — search is purely client-side
  const [allDbItems, setAllDbItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [customSize, setCustomSize] = useState(""); // free-form size for coverage items
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [quantity, setQuantity] = useState(0);
  const [reorderPoint, setReorderPoint] = useState(0);
  const [maxStock, setMaxStock] = useState("");
  const [storageZone, setStorageZone] = useState("");
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [duplicateError, setDuplicateError] = useState("");

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const isCoverage = selectedItem && isCoverageUom(selectedItem.uom);

  // Load all active material items once when the modal opens
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE}/item-database?category=M&isActive=true&limit=5000`,
          { headers },
        );
        const items = res.data.items || [];
        clearSearchCache(items); // reset any stale blobs
        if (!cancelled) setAllDbItems(items);
      } catch (err) {
        console.error("Failed to load item database:", err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Client-side filtered results using multiTokenSearch
  const filteredItems = useMemo(() => {
    if (!search.trim()) return allDbItems.slice(0, 80); // show first 80 when no query
    return multiTokenSearch(allDbItems, search).slice(0, 80);
  }, [allDbItems, search]);

  // When an item is selected, pre-fill size from its DB default (user can edit)
  const handleSelectItem = (item) => {
    setSelectedItem(item);
    setCustomSize(item.size || "");
    setDuplicateError("");
  };

  // Live duplicate check: itemDatabaseId + warehouse + size (case-insensitive trim)
  useEffect(() => {
    if (!selectedItem || !selectedWarehouse) {
      setDuplicateError("");
      return;
    }
    const sizeToCheck = isCoverageUom(selectedItem.uom)
      ? customSize.trim().toLowerCase()
      : "";
    const isDupe = existingEntries.some(
      (e) =>
        e.itemDatabaseId === selectedItem._id &&
        e.warehouse === selectedWarehouse &&
        (e.size || "").trim().toLowerCase() === sizeToCheck,
    );
    if (isDupe) {
      const sizePart = customSize.trim() ? ` (${customSize.trim()})` : "";
      const whName =
        warehouses.find((w) => w._id === selectedWarehouse)?.name ||
        "that warehouse";
      setDuplicateError(
        `${selectedItem.itemCode}${sizePart} is already tracked in ${whName}. Adjust its quantity instead.`,
      );
    } else {
      setDuplicateError("");
    }
  }, [
    selectedItem,
    selectedWarehouse,
    customSize,
    existingEntries,
    warehouses,
  ]);

  const warehouseOptions = warehouses
    .filter((w) => w.isActive)
    .map((w) => ({ value: w._id, label: `${w.code} — ${w.name}` }));

  // For coverage items the size must be non-empty AND parseable so coverage is never null
  const computedCoverage =
    selectedItem && isCoverageUom(selectedItem.uom)
      ? parseCoverage(customSize, selectedItem.uom)
      : null;

  const canSave =
    !!selectedItem &&
    !!selectedWarehouse &&
    !duplicateError &&
    // Coverage items: size must be entered and parseable
    (!isCoverageUom(selectedItem?.uom) ||
      (customSize.trim().length > 0 && computedCoverage !== null));

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-2xl p-6">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-white">
            Add Item to Inventory
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* ── Step 1: Search & pick item ── */}
        {!selectedItem ? (
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Search Item Database (Materials only)
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={
                  isLoading
                    ? "Loading items…"
                    : "Search code, description, size, vendor… (e.g. 358, 20ga, 4x8)"
                }
                disabled={isLoading}
                className="w-full pl-9 pr-10 py-2 rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm focus:border-orange-500 focus:outline-none disabled:opacity-60"
                autoFocus
              />
              {isLoading && (
                <ArrowPathIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
              )}
              {!isLoading && search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* result count hint */}
            {!isLoading && allDbItems.length > 0 && (
              <p className="text-[10px] text-gray-500 mt-1 text-right">
                {search.trim()
                  ? `${filteredItems.length} of ${allDbItems.length} items`
                  : `${allDbItems.length} items — type to filter`}
              </p>
            )}

            {!isLoading && filteredItems.length > 0 && (
              <div className="mt-1 bg-gray-700 border border-gray-600 rounded-lg max-h-72 overflow-y-auto">
                {filteredItems.map((item) => (
                  <div
                    key={item._id}
                    onClick={() => handleSelectItem(item)}
                    className="px-4 py-3 hover:bg-gray-600 cursor-pointer border-b border-gray-600 last:border-0"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm text-white font-mono">
                            {item.itemCode}
                          </p>
                          {isCoverageUom(item.uom) && (
                            <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30 rounded uppercase tracking-wide">
                              {item.uom} · size required
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {item.description}
                        </p>
                      </div>
                      <div className="text-right ml-4 shrink-0">
                        <p className="text-xs text-gray-500">{item.uom}</p>
                        {item.size && (
                          <p className="text-xs font-mono text-gray-400">
                            default: {item.size}
                          </p>
                        )}
                        {item.coveragePerUnit && (
                          <p className="text-[10px] text-gray-500">
                            {item.coveragePerUnit} {item.uom}/ea
                          </p>
                        )}
                        {isCoverageUom(item.uom) ? (
                          <p className="text-xs text-green-400 font-mono mt-0.5">
                            ${(item.defaultCost || 0).toFixed(4)}/{item.uom}
                          </p>
                        ) : (
                          <p className="text-xs text-green-400 font-mono mt-0.5">
                            ${(item.defaultCost || 0).toFixed(2)}/ea
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!isLoading && search.trim() && filteredItems.length === 0 && (
              <p className="text-sm text-gray-500 mt-3 text-center">
                No matching material items found
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Selected item banner */}
            <div className="flex items-center justify-between p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-mono text-orange-400">
                    {selectedItem.itemCode}
                  </p>
                  <span className="text-xs text-gray-400 bg-gray-700 px-1.5 py-0.5 rounded">
                    {selectedItem.uom}
                  </span>
                  {isCoverage && (
                    <span className="text-[10px] font-semibold text-blue-300 bg-blue-500/20 border border-blue-500/30 px-1.5 py-0.5 rounded uppercase tracking-wide">
                      coverage item
                    </span>
                  )}
                </div>
                <p className="text-sm text-white mt-0.5">
                  {selectedItem.description}
                </p>
                {isCoverage &&
                  parseCoverage(customSize, selectedItem.uom) !== null && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {parseCoverage(customSize, selectedItem.uom)}{" "}
                      {selectedItem.uom} per piece
                    </p>
                  )}
              </div>
              <button
                onClick={() => {
                  setSelectedItem(null);
                  setCustomSize("");
                  setDuplicateError("");
                }}
                className="text-gray-400 hover:text-white ml-4 shrink-0"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            {/* ── Size field for coverage items ── */}
            {isCoverage &&
              (() => {
                const computed = parseCoverage(customSize, selectedItem.uom);
                return (
                  <div className="p-3 bg-gray-700/60 border border-gray-600 rounded-lg">
                    <label className="block text-xs font-semibold text-gray-300 mb-1">
                      Size *
                      <span className="ml-1 font-normal text-gray-500">
                        — each size is a separate inventory row; coverage is
                        calculated automatically from the dimensions you enter
                      </span>
                    </label>

                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={customSize}
                        onChange={(e) => setCustomSize(e.target.value)}
                        placeholder={
                          selectedItem.uom === "LF"
                            ? "e.g. 8'  or  5'5"
                            : "e.g. 4'x8'"
                        }
                        className={`flex-1 rounded border text-sm px-3 py-2 font-mono focus:outline-none ${
                          !customSize.trim()
                            ? "bg-red-900/20 border-red-500/50 text-gray-200 focus:border-red-400"
                            : computed !== null
                              ? "bg-gray-600 border-green-600/40 text-gray-200 focus:border-orange-500"
                              : "bg-gray-600 border-yellow-600/40 text-gray-200 focus:border-orange-500"
                        }`}
                        autoFocus
                      />
                      {selectedItem.size &&
                        customSize !== selectedItem.size && (
                          <button
                            type="button"
                            onClick={() => setCustomSize(selectedItem.size)}
                            className="px-2 py-1.5 text-xs text-gray-400 bg-gray-600 border border-gray-500 rounded hover:bg-gray-500 hover:text-white whitespace-nowrap"
                          >
                            Reset ({selectedItem.size})
                          </button>
                        )}
                    </div>

                    {/* Computed coverage — read-only, always derived from size */}
                    <div className="mt-2 flex items-center gap-2 min-h-[20px]">
                      {!customSize.trim() && (
                        <p className="text-xs text-red-400">
                          Size is required for {selectedItem.uom} items
                        </p>
                      )}
                      {customSize.trim() && computed !== null && (
                        <p className="text-xs text-green-400">
                          ✓ {computed} {selectedItem.uom} per piece
                        </p>
                      )}
                      {customSize.trim() && computed === null && (
                        <p className="text-xs text-yellow-400">
                          ⚠ Could not calculate coverage — check the format
                          (e.g.{" "}
                          {selectedItem.uom === "LF" ? "8' or 96" : "4'x8'"})
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}

            {/* ── Duplicate error ── */}
            {duplicateError && (
              <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-300">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                {duplicateError}
              </div>
            )}

            {/* ── Detail fields ── */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Warehouse / Location *
                </label>
                <Select
                  options={warehouseOptions}
                  value={warehouseOptions.find(
                    (o) => o.value === selectedWarehouse,
                  )}
                  onChange={(opt) => setSelectedWarehouse(opt?.value || null)}
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  placeholder="Select warehouse…"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1">
                  Zone / Bin (optional)
                </label>
                <input
                  type="text"
                  value={storageZone}
                  onChange={(e) => setStorageZone(e.target.value)}
                  className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 font-mono focus:border-orange-500 focus:outline-none"
                  placeholder="A3-B12"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 flex items-center">
                  Opening Quantity
                  <span className="ml-1 text-gray-500 font-normal">
                    (
                    {isCoverage && customSize.trim()
                      ? `${customSize.trim()} · `
                      : ""}
                    {selectedItem.uom})
                  </span>
                  <InfoTip text="How many pieces you currently have on the shelf right now. The size and unit of measure in brackets confirms exactly what you are counting — for SF or LF items you are counting pieces, not square feet." />
                </label>
                <input
                  type="number"
                  min="0"
                  value={quantity}
                  onChange={(e) => setQuantity(parseFloat(e.target.value) || 0)}
                  className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 flex items-center">
                  Reorder Point
                  <InfoTip text="The minimum quantity you want on hand before you need to reorder. When stock drops to or below this number the row shows as Low Stock. Set to 0 if you do not need low-stock alerts for this item." />
                </label>
                <input
                  type="number"
                  min="0"
                  value={reorderPoint}
                  onChange={(e) =>
                    setReorderPoint(parseFloat(e.target.value) || 0)
                  }
                  className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 flex items-center">
                  Max Stock (optional)
                  <InfoTip text="An optional ceiling on how much of this item you want to store. Not enforced as a hard block — it is a reference number for purchasing decisions or to flag accidental over-receiving. Leave blank if not needed." />
                </label>
                <input
                  type="number"
                  min="0"
                  value={maxStock}
                  onChange={(e) => setMaxStock(e.target.value)}
                  className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none"
                  placeholder="—"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1 flex items-center">
                  Notes
                  <InfoTip text="Any free-form text relevant to this inventory entry — storage notes, supplier memos, reminders about a particular batch, etc. Does not affect any calculations." />
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none"
                  placeholder="Optional notes…"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!canSave) return;
                  onSave({
                    itemDatabaseId: selectedItem._id,
                    itemCode: selectedItem.itemCode,
                    description: selectedItem.description,
                    uom: selectedItem.uom,
                    // For coverage items use the user-entered size; for others keep DB size
                    size: isCoverageUom(selectedItem.uom)
                      ? customSize.trim()
                      : selectedItem.size || "",
                    defaultCost: selectedItem.defaultCost,
                    // Coverage always derived from size — never manual, never empty for coverage items
                    coveragePerUnit: isCoverageUom(selectedItem.uom)
                      ? String(computedCoverage)
                      : selectedItem.coveragePerUnit || "",
                    warehouse: selectedWarehouse,
                    storageZone,
                    quantity,
                    reorderPoint,
                    maxStock: maxStock !== "" ? parseFloat(maxStock) : null,
                    notes,
                  });
                }}
                disabled={!canSave}
                className="px-4 py-2 text-sm text-white bg-orange-500 rounded hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add to Inventory
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Adjustment Modal ─────────────────────────────────────────────────────────
function AdjustmentModal({ item, warehouses, onSave, onClose }) {
  const [adjType, setAdjType] = useState("add");
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [targetWarehouse, setTargetWarehouse] = useState(null);
  const [referenceNumber, setReferenceNumber] = useState("");

  const warehouseOptions = warehouses
    .filter(
      (w) =>
        w.isActive && w._id !== item.warehouse?._id && w._id !== item.warehouse,
    )
    .map((w) => ({ value: w._id, label: `${w.code} — ${w.name}` }));

  const isTransfer = adjType === "transfer";
  const newQty =
    adjType === "add"
      ? item.quantity + amount
      : adjType === "remove"
        ? Math.max(0, item.quantity - amount)
        : adjType === "set"
          ? amount
          : item.quantity; // transfer doesn't change source qty shown here

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-lg p-6">
        <div className="flex justify-between items-center mb-5">
          <div>
            <h2 className="text-lg font-semibold text-white">Adjust Stock</h2>
            <p className="text-sm text-gray-400 font-mono">
              {item.itemCode} — {item.description}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Current stock display */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-gray-700 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400">Current</p>
            <p className="text-2xl font-bold text-white">{item.quantity}</p>
            <p className="text-xs text-gray-500">{item.uom}</p>
          </div>
          <div className="bg-gray-700 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400">Adjustment</p>
            <p
              className={`text-2xl font-bold ${adjType === "add" ? "text-green-400" : adjType === "remove" ? "text-red-400" : "text-blue-400"}`}
            >
              {adjType === "add" ? "+" : adjType === "remove" ? "-" : "→"}
              {amount}
            </p>
            <p className="text-xs text-gray-500">{item.uom}</p>
          </div>
          <div className="bg-gray-700 rounded-lg p-3 text-center">
            <p className="text-xs text-gray-400">After</p>
            <p
              className={`text-2xl font-bold ${newQty < item.reorderPoint ? "text-red-400" : "text-green-400"}`}
            >
              {isTransfer ? item.quantity : newQty}
            </p>
            <p className="text-xs text-gray-500">{item.uom}</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Adjustment type buttons */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-2">
              Type
            </label>
            <div className="grid grid-cols-4 gap-2">
              {ADJUSTMENT_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setAdjType(t.value)}
                  className={`px-2 py-2 text-xs rounded border transition-colors ${
                    adjType === t.value
                      ? "border-orange-500 bg-orange-500/20 text-orange-300"
                      : "border-gray-600 text-gray-400 hover:border-gray-500"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                {adjType === "set" ? "New Quantity" : "Amount"}
              </label>
              <input
                type="number"
                min="0"
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Reason
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none"
              >
                <option value="">Select reason...</option>
                {ADJUSTMENT_REASONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isTransfer && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Transfer To Warehouse *
              </label>
              <Select
                options={warehouseOptions}
                value={warehouseOptions.find(
                  (o) => o.value === targetWarehouse,
                )}
                onChange={(opt) => setTargetWarehouse(opt?.value || null)}
                styles={selectStyles}
                menuPortalTarget={document.body}
                placeholder="Select destination..."
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Reference # (PO/MO/etc.)
              </label>
              <input
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 font-mono focus:border-orange-500 focus:outline-none"
                placeholder="PO-001, MO-042..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Notes
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded bg-gray-700 border border-gray-600 text-gray-200 text-sm px-3 py-2 focus:border-orange-500 focus:outline-none"
                placeholder="Optional notes..."
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (!reason || (isTransfer && !targetWarehouse)) return;
              onSave({
                adjustmentType: adjType,
                amount,
                reason,
                notes,
                referenceNumber,
                targetWarehouse: isTransfer ? targetWarehouse : null,
              });
            }}
            disabled={!reason || (isTransfer && !targetWarehouse)}
            className="px-4 py-2 text-sm text-white bg-orange-500 rounded hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply Adjustment
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── History Panel ────────────────────────────────────────────────────────────
function HistoryPanel({ history, uom, onClose }) {
  const typeColors = {
    add: "text-green-400 bg-green-500/10 border-green-500/20",
    remove: "text-red-400 bg-red-500/10 border-red-500/20",
    set: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    transfer: "text-purple-400 bg-purple-500/10 border-purple-500/20",
    "stock-out": "text-orange-400 bg-orange-500/10 border-orange-500/20",
    initial: "text-gray-400 bg-gray-500/10 border-gray-500/20",
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl w-full max-w-2xl p-6 max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-white">Stock History</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 space-y-2 pr-1">
          {history.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              No history yet
            </p>
          ) : (
            [...history].reverse().map((entry, idx) => (
              <div
                key={idx}
                className="flex gap-3 p-3 bg-gray-700/50 rounded-lg border border-gray-700"
              >
                <div
                  className={`mt-0.5 px-2 py-0.5 text-[10px] font-bold uppercase rounded border h-fit ${typeColors[entry.type] || typeColors.initial}`}
                >
                  {entry.type}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <span className="text-sm text-white">
                        {entry.type === "add" && `+${entry.amount} ${uom}`}
                        {entry.type === "remove" && `-${entry.amount} ${uom}`}
                        {entry.type === "set" &&
                          `Set to ${entry.amount} ${uom}`}
                        {entry.type === "transfer" &&
                          `Transferred ${entry.amount} ${uom}`}
                        {entry.type === "stock-out" &&
                          `Stock-out: ${entry.amount} ${uom}`}
                        {entry.type === "initial" &&
                          `Opening stock: ${entry.amount} ${uom}`}
                      </span>
                      {entry.reason && (
                        <span className="ml-2 text-xs text-gray-400">
                          (
                          {ADJUSTMENT_REASONS.find(
                            (r) => r.value === entry.reason,
                          )?.label || entry.reason}
                          )
                        </span>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-gray-400">
                        {entry.qtyBefore} →{" "}
                        <span className="text-white">{entry.qtyAfter}</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    {entry.notes && (
                      <span className="text-xs text-gray-500 italic">
                        {entry.notes}
                      </span>
                    )}
                    {entry.targetWarehouse && (
                      <span className="text-xs text-purple-400">
                        → {entry.targetWarehouseName}
                      </span>
                    )}
                    {console.log(entry)}
                    {/* NEW — project and order context for stock-out entries */}
                    {entry.type === "stock-out" &&
                      (entry.projectName || entry.stockOutOrderNumber) && (
                        <div className="flex items-center gap-2 flex-wrap mt-1">
                          {entry.projectName && (
                            <span className="text-xs text-blue-400/80">
                              {entry.projectName}
                            </span>
                          )}
                          {entry.stockOutOrderNumber && (
                            <span className="text-xs font-mono text-orange-400/80">
                              SO-{entry.stockOutOrderNumber}
                            </span>
                          )}
                          {entry.stockOutOrderPoNumber && (
                            <span className="text-xs font-mono text-gray-400/80">
                              PO#{entry.stockOutOrderPoNumber}
                            </span>
                          )}
                        </div>
                      )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <ClockIcon className="h-3 w-3 text-gray-500" />
                    <span className="text-xs text-gray-500">
                      {new Date(entry.timestamp).toLocaleString("en-CA", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </span>
                    {entry.updatedBy && (
                      <span className="text-xs text-gray-500">
                        by{" "}
                        <span className="text-gray-400">{entry.updatedBy}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function InventoryManager() {
  const [inventoryItems, setInventoryItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("inventory"); // "inventory" | "warehouses"
  const [showAddItem, setShowAddItem] = useState(false);
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [adjustingItem, setAdjustingItem] = useState(null);
  const [historyItem, setHistoryItem] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [filters, setFilters] = useState({
    search: "",
    warehouse: [],
    stockStatus: [],
  });
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const showAlert = (type, title, message, onConfirm) => {
    setAlert({
      open: true,
      type,
      title,
      message,
      onConfirm: onConfirm || (() => setAlert((p) => ({ ...p, open: false }))),
    });
  };

  // ─── Fetch ────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [invRes, whRes] = await Promise.all([
        axios.get(`${API_BASE}/inventory`, { headers }),
        axios.get(`${API_BASE}/inventory/warehouses`, { headers }),
      ]);
      setInventoryItems(invRes.data.items || []);
      setWarehouses(whRes.data.warehouses || []);
    } catch (err) {
      // If API doesn't exist yet, use local state only
      console.warn("Inventory API not available yet — using local state");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Warehouse CRUD ───────────────────────────────────────────────
  const handleSaveWarehouse = async (form) => {
    try {
      if (editingWarehouse) {
        const res = await axios.put(
          `${API_BASE}/inventory/warehouses/${editingWarehouse._id}`,
          form,
          { headers },
        );
        setWarehouses((prev) =>
          prev.map((w) =>
            w._id === editingWarehouse._id ? res.data.warehouse : w,
          ),
        );
        showAlert("success", "Updated", "Warehouse updated successfully");
      } else {
        const res = await axios.post(`${API_BASE}/inventory/warehouses`, form, {
          headers,
        });
        setWarehouses((prev) => [...prev, res.data.warehouse]);
        showAlert("success", "Created", "Warehouse created successfully");
      }
      setShowWarehouseModal(false);
      setEditingWarehouse(null);
    } catch (err) {
      // Optimistic local update for dev preview
      if (editingWarehouse) {
        setWarehouses((prev) =>
          prev.map((w) =>
            w._id === editingWarehouse._id ? { ...w, ...form } : w,
          ),
        );
      } else {
        setWarehouses((prev) => [
          ...prev,
          { ...form, _id: `local-${Date.now()}` },
        ]);
      }
      setShowWarehouseModal(false);
      setEditingWarehouse(null);
    }
  };

  const handleDeleteWarehouse = (wh) => {
    showAlert(
      "warning",
      "Delete Warehouse?",
      `Remove "${wh.name}"? This will not delete inventory items — they will become unassigned.`,
      async () => {
        try {
          await axios.delete(`${API_BASE}/inventory/warehouses/${wh._id}`, {
            headers,
          });
        } catch (_) {}
        setWarehouses((prev) => prev.filter((w) => w._id !== wh._id));
        showAlert("success", "Deleted", "Warehouse removed");
      },
    );
  };

  // ─── Inventory CRUD ───────────────────────────────────────────────
  const handleAddItem = async (form) => {
    try {
      const res = await axios.post(`${API_BASE}/inventory/items`, form, {
        headers,
      });
      setInventoryItems((prev) => [...prev, res.data.item]);
    } catch (_) {
      // Local preview
      const warehouse = warehouses.find((w) => w._id === form.warehouse);
      const newItem = {
        ...form,
        _id: `local-${Date.now()}`,
        warehouse: {
          _id: form.warehouse,
          name: warehouse?.name,
          code: warehouse?.code,
        },
        history:
          form.quantity > 0
            ? [
                {
                  type: "initial",
                  amount: form.quantity,
                  qtyBefore: 0,
                  qtyAfter: form.quantity,
                  reason: "initial",
                  timestamp: new Date(),
                  updatedBy: "You",
                },
              ]
            : [],
        lastUpdatedBy: "You",
        lastUpdatedAt: new Date(),
        createdAt: new Date(),
      };
      setInventoryItems((prev) => [...prev, newItem]);
    }
    setShowAddItem(false);
    showAlert("success", "Added", "Item added to inventory");
  };

  const handleAdjustment = async (item, adjustData) => {
    const {
      adjustmentType,
      amount,
      reason,
      notes,
      referenceNumber,
      targetWarehouse,
    } = adjustData;
    let newQty = item.quantity;
    if (adjustmentType === "add") newQty = item.quantity + amount;
    else if (adjustmentType === "remove")
      newQty = Math.max(0, item.quantity - amount);
    else if (adjustmentType === "set") newQty = amount;

    const historyEntry = {
      type: adjustmentType,
      amount,
      reason,
      notes,
      referenceNumber,
      qtyBefore: item.quantity,
      qtyAfter: adjustmentType === "transfer" ? item.quantity : newQty,
      timestamp: new Date(),
      updatedBy: "You",
      targetWarehouse,
      targetWarehouseName:
        warehouses.find((w) => w._id === targetWarehouse)?.name || "",
    };

    try {
      const res = await axios.post(
        `${API_BASE}/inventory/items/${item._id}/adjust`,
        adjustData,
        { headers },
      );
      setInventoryItems((prev) =>
        prev.map((i) => (i._id === item._id ? res.data.item : i)),
      );
    } catch (_) {
      // Local update
      setInventoryItems((prev) =>
        prev.map((i) =>
          i._id === item._id
            ? {
                ...i,
                quantity:
                  adjustmentType === "transfer" ? item.quantity : newQty,
                history: [...(i.history || []), historyEntry],
                lastUpdatedBy: "You",
                lastUpdatedAt: new Date(),
              }
            : i,
        ),
      );
    }

    setAdjustingItem(null);
    showAlert("success", "Adjusted", "Stock level updated");
  };

  const handleDeleteItem = (item) => {
    showAlert(
      "warning",
      "Remove from Inventory?",
      `Remove "${item.description}" from inventory tracking? This won't delete it from the Item Database.`,
      async () => {
        try {
          await axios.delete(`${API_BASE}/inventory/items/${item._id}`, {
            headers,
          });
        } catch (_) {}
        setInventoryItems((prev) => prev.filter((i) => i._id !== item._id));
        showAlert("success", "Removed", "Item removed from inventory");
      },
    );
  };

  // ─── Filters + derived data ───────────────────────────────────────
  const warehouseFilterOptions = useMemo(() => {
    return warehouses.map((w) => ({
      value: w._id,
      label: `${w.code} — ${w.name}`,
    }));
  }, [warehouses]);

  const filteredItems = useMemo(() => {
    let result = inventoryItems;

    if (filters.search.trim()) {
      const q = filters.search.toLowerCase();
      result = result.filter(
        (item) =>
          (item.itemCode || "").toLowerCase().includes(q) ||
          (item.description || "").toLowerCase().includes(q) ||
          (item.storageZone || "").toLowerCase().includes(q),
      );
    }

    if (filters.warehouse.length > 0) {
      result = result.filter((item) => {
        const whId = item.warehouse?._id || item.warehouse;
        return filters.warehouse.includes(whId);
      });
    }

    if (filters.stockStatus.length > 0) {
      result = result.filter((item) => {
        if (filters.stockStatus.includes("out") && item.quantity <= 0)
          return true;
        if (
          filters.stockStatus.includes("low") &&
          item.quantity > 0 &&
          item.quantity <= item.reorderPoint
        )
          return true;
        if (
          filters.stockStatus.includes("ok") &&
          item.quantity > item.reorderPoint
        )
          return true;
        return false;
      });
    }

    return result;
  }, [inventoryItems, filters]);

  const summary = useMemo(() => {
    return {
      total: inventoryItems.length,
      out: inventoryItems.filter((i) => i.quantity <= 0).length,
      low: inventoryItems.filter(
        (i) => i.quantity > 0 && i.quantity <= i.reorderPoint,
      ).length,
      ok: inventoryItems.filter((i) => i.quantity > i.reorderPoint).length,
      totalValue: inventoryItems.reduce((sum, i) => sum + calcItemValue(i), 0),
    };
  }, [inventoryItems]);

  // For duplicate detection: track itemDatabaseId + warehouse + size per existing entry
  const existingEntries = useMemo(
    () =>
      inventoryItems.map((i) => ({
        itemDatabaseId: i.itemDatabaseId || i._id,
        warehouse: i.warehouse?._id || i.warehouse,
        size: (i.size || "").toLowerCase(),
      })),
    [inventoryItems],
  );

  const getStockStatus = (item) => {
    if (item.quantity <= 0)
      return {
        label: "Out of Stock",
        class: "bg-red-900/50 text-red-300 border-red-700",
      };
    if (item.quantity <= item.reorderPoint)
      return {
        label: "Low Stock",
        class: "bg-yellow-900/50 text-yellow-300 border-yellow-700",
      };
    return {
      label: "In Stock",
      class: "bg-green-900/50 text-green-300 border-green-700",
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <ArrowPathIcon className="h-6 w-6 text-orange-500 animate-spin" />
        <span className="ml-3 text-gray-400">Loading inventory...</span>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto px-4 py-6">
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert((p) => ({ ...p, open: false }))}
        onConfirm={alert.onConfirm}
      />

      {showAddItem && (
        <AddInventoryItemModal
          warehouses={warehouses}
          existingEntries={existingEntries}
          onSave={handleAddItem}
          onClose={() => setShowAddItem(false)}
        />
      )}

      {(showWarehouseModal || editingWarehouse) && (
        <WarehouseModal
          warehouse={editingWarehouse}
          onSave={handleSaveWarehouse}
          onClose={() => {
            setShowWarehouseModal(false);
            setEditingWarehouse(null);
          }}
        />
      )}

      {adjustingItem && (
        <AdjustmentModal
          item={adjustingItem}
          warehouses={warehouses}
          onSave={(data) => handleAdjustment(adjustingItem, data)}
          onClose={() => setAdjustingItem(null)}
        />
      )}

      {historyItem && (
        <HistoryPanel
          history={historyItem.history || []}
          uom={historyItem.uom}
          onClose={() => setHistoryItem(null)}
        />
      )}

      {/* ── Header ── */}
      <div className="mb-6 bg-gray-700 rounded-lg p-6 border border-gray-600 shadow-lg">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold text-white">Inventory</h1>
            <p className="text-sm text-gray-400 mt-1">
              Track material stock levels across warehouses
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                setShowWarehouseModal(true);
                setEditingWarehouse(null);
              }}
              className="px-4 py-2 text-sm text-white bg-gray-600 rounded hover:bg-gray-500 flex items-center gap-2 border border-gray-500"
            >
              <BuildingStorefrontIcon className="h-4 w-4" />
              Manage Warehouses
            </button>
            <button
              onClick={() => setShowAddItem(true)}
              className="px-4 py-2 text-sm text-white bg-orange-500 rounded hover:bg-orange-600 flex items-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              Add Item
            </button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
          {[
            { label: "Total Items", value: summary.total, color: "text-white" },
            { label: "In Stock", value: summary.ok, color: "text-green-400" },
            {
              label: "Low Stock",
              value: summary.low,
              color: "text-yellow-400",
            },
            {
              label: "Out of Stock",
              value: summary.out,
              color: "text-red-400",
            },
            {
              label: "Total Value",
              value: `$${summary.totalValue.toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
              color: "text-orange-400",
            },
          ].map((card) => (
            <div
              key={card.label}
              className="bg-gray-800/60 rounded-lg p-3 border border-gray-600"
            >
              <p className="text-xs text-gray-400">{card.label}</p>
              <p className={`text-xl font-bold ${card.color} mt-0.5`}>
                {card.value}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 mb-4">
        {[
          { id: "inventory", label: "Inventory Items", icon: CubeIcon },
          {
            id: "warehouses",
            label: "Warehouses",
            icon: BuildingStorefrontIcon,
          },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm rounded-lg border transition-colors ${
              activeTab === id
                ? "bg-orange-500 border-orange-500/50 text-white"
                : "border-gray-600 text-gray-400 hover:text-white hover:border-gray-500 bg-gray-700"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════ */}
      {/* TAB: INVENTORY ITEMS                       */}
      {/* ══════════════════════════════════════════ */}
      {activeTab === "inventory" && (
        <>
          {/* Filter bar */}
          <div className="mb-4 bg-gray-700 rounded-lg p-4 border border-gray-600">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, search: e.target.value }))
                  }
                  placeholder="Search code, description, zone..."
                  className="w-full pl-9 pr-4 py-2 rounded bg-gray-600 border border-gray-500 text-gray-200 text-sm focus:border-orange-500 focus:outline-none"
                />
              </div>

              <div className="min-w-[200px]">
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  options={warehouseFilterOptions}
                  value={warehouseFilterOptions.filter((o) =>
                    filters.warehouse.includes(o.value),
                  )}
                  onChange={(opts) =>
                    setFilters((f) => ({
                      ...f,
                      warehouse: opts ? opts.map((o) => o.value) : [],
                    }))
                  }
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  placeholder="All Warehouses..."
                />
              </div>

              <div className="flex gap-2">
                {[
                  {
                    value: "ok",
                    label: "In Stock",
                    cls: "bg-green-900/40 text-green-300 border-green-700",
                  },
                  {
                    value: "low",
                    label: "Low Stock",
                    cls: "bg-yellow-900/40 text-yellow-300 border-yellow-700",
                  },
                  {
                    value: "out",
                    label: "Out of Stock",
                    cls: "bg-red-900/40 text-red-300 border-red-700",
                  },
                ].map((status) => (
                  <button
                    key={status.value}
                    onClick={() =>
                      setFilters((f) => ({
                        ...f,
                        stockStatus: f.stockStatus.includes(status.value)
                          ? f.stockStatus.filter((s) => s !== status.value)
                          : [...f.stockStatus, status.value],
                      }))
                    }
                    className={`px-3 py-1.5 text-xs rounded border transition-all ${
                      filters.stockStatus.includes(status.value)
                        ? status.cls
                        : "border-gray-600 text-gray-400 hover:border-gray-500"
                    }`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>

              {(filters.search ||
                filters.warehouse.length > 0 ||
                filters.stockStatus.length > 0) && (
                <button
                  onClick={() =>
                    setFilters({ search: "", warehouse: [], stockStatus: [] })
                  }
                  className="px-3 py-1.5 text-xs text-orange-300 bg-orange-900/30 border border-orange-700 rounded hover:bg-orange-900/50"
                >
                  Clear
                </button>
              )}

              <span className="ml-auto text-sm text-gray-400">
                <span className="text-white font-semibold">
                  {filteredItems.length}
                </span>{" "}
                of {inventoryItems.length} items
              </span>
            </div>
          </div>

          {/* Inventory table */}
          <div className="bg-gray-700 rounded-lg border border-gray-600 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-600">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Item
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Warehouse
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Zone / Bin
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                      Qty
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                      Reorder Pt
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                      Cost / Piece
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                      Value
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                      Last Updated
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-4 py-12 text-center text-gray-400"
                      >
                        {inventoryItems.length === 0 ? (
                          <div>
                            <CubeIcon className="h-10 w-10 mx-auto text-gray-600 mb-3" />
                            <p className="text-sm">No inventory items yet</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Add material items from the Item Database to start
                              tracking
                            </p>
                          </div>
                        ) : (
                          "No items match your filters"
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item) => {
                      const status = getStockStatus(item);
                      const costPerPiece = calcCostPerPiece(item);
                      const value = calcItemValue(item);
                      const whName =
                        item.warehouse?.name ||
                        warehouses.find((w) => w._id === item.warehouse)
                          ?.name ||
                        "—";
                      const whCode =
                        item.warehouse?.code ||
                        warehouses.find((w) => w._id === item.warehouse)
                          ?.code ||
                        "";
                      const lastBy = item.lastUpdatedBy || "—";
                      const lastAt = item.lastUpdatedAt
                        ? new Date(item.lastUpdatedAt).toLocaleString("en-CA", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })
                        : "—";

                      return (
                        <tr key={item._id} className="hover:bg-gray-650 group">
                          <td className="px-4 py-3">
                            <div className="flex items-start gap-2">
                              <div
                                className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                                  item.quantity <= 0
                                    ? "bg-red-500"
                                    : item.quantity <= item.reorderPoint
                                      ? "bg-yellow-500"
                                      : "bg-green-500"
                                }`}
                              />
                              <div>
                                <p className="text-sm font-mono text-gray-300">
                                  {item.itemCode}
                                </p>
                                <p className="text-sm text-white">
                                  {item.description}
                                </p>
                                {item.size && (
                                  <p className="text-xs text-gray-500">
                                    {item.size}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm text-white">{whName}</p>
                              {whCode && (
                                <p className="text-xs font-mono text-gray-500">
                                  {whCode}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm font-mono text-gray-400">
                            {item.storageZone || "—"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span
                              className={`text-lg font-bold ${item.quantity <= 0 ? "text-red-400" : item.quantity <= item.reorderPoint ? "text-yellow-400" : "text-white"}`}
                            >
                              {item.quantity}
                            </span>
                            <span className="text-xs text-gray-500 ml-1">
                              {item.uom === "LF" || item.uom == "SF"
                                ? "PC"
                                : item.uom}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-gray-400">
                            {item.reorderPoint || 0}
                            <span className="text-xs ml-1 text-gray-600">
                              {item.uom === "LF" || item.uom == "SF"
                                ? "PC"
                                : item.uom}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-mono text-gray-300">
                            <div>${costPerPiece.toFixed(2)}</div>
                            {isCoverageUom(item.uom) &&
                              item.coveragePerUnit && (
                                <div className="text-[10px] text-gray-500">
                                  ${(item.defaultCost || 0).toFixed(4)}/
                                  {item.uom} × {item.coveragePerUnit}
                                </div>
                              )}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-mono text-green-400">
                            $
                            {value.toLocaleString("en-CA", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${status.class}`}
                            >
                              {status.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-xs text-gray-400">{lastBy}</p>
                              <p className="text-xs text-gray-600">{lastAt}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => setAdjustingItem(item)}
                                title="Adjust Stock"
                                className="p-1.5 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 rounded"
                              >
                                <ArrowUpIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => setHistoryItem(item)}
                                title="View History"
                                className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded"
                              >
                                <ClockIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteItem(item)}
                                title="Remove from Inventory"
                                className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════════ */}
      {/* TAB: WAREHOUSES                            */}
      {/* ══════════════════════════════════════════ */}
      {activeTab === "warehouses" && (
        <div className="space-y-3">
          {warehouses.length === 0 ? (
            <div className="bg-gray-700 rounded-lg border border-gray-600 p-12 text-center">
              <BuildingStorefrontIcon className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">No warehouses configured yet</p>
              <p className="text-xs text-gray-500 mt-1">
                Add a warehouse to start assigning inventory locations
              </p>
              <button
                onClick={() => setShowWarehouseModal(true)}
                className="mt-4 px-4 py-2 text-sm text-white bg-orange-500 rounded hover:bg-orange-600"
              >
                Add First Warehouse
              </button>
            </div>
          ) : (
            warehouses.map((wh) => {
              const itemsInWh = inventoryItems.filter(
                (i) => (i.warehouse?._id || i.warehouse) === wh._id,
              );
              const totalValue = itemsInWh.reduce(
                (sum, i) => sum + calcItemValue(i),
                0,
              );
              const lowItems = itemsInWh.filter(
                (i) => i.quantity > 0 && i.quantity <= i.reorderPoint,
              ).length;
              const outItems = itemsInWh.filter((i) => i.quantity <= 0).length;

              return (
                <div
                  key={wh._id}
                  className="bg-gray-700 rounded-lg border border-gray-600 overflow-hidden"
                >
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-orange-500/20 border border-orange-500/30 flex items-center justify-center">
                        <BuildingStorefrontIcon className="h-5 w-5 text-orange-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold text-white">
                            {wh.name}
                          </h3>
                          <span className="px-2 py-0.5 text-[10px] font-mono bg-gray-600 text-gray-300 rounded border border-gray-500">
                            {wh.code}
                          </span>
                          {!wh.isActive && (
                            <span className="px-2 py-0.5 text-[10px] bg-gray-600 text-gray-400 rounded">
                              Inactive
                            </span>
                          )}
                        </div>
                        {wh.address && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            {typeof wh.address === "object"
                              ? wh.address.formattedAddress || ""
                              : wh.address}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-lg font-bold text-white">
                          {itemsInWh.length}
                        </p>
                        <p className="text-[10px] text-gray-400 uppercase">
                          Items
                        </p>
                      </div>
                      {lowItems > 0 && (
                        <div className="text-center">
                          <p className="text-lg font-bold text-yellow-400">
                            {lowItems}
                          </p>
                          <p className="text-[10px] text-gray-400 uppercase">
                            Low
                          </p>
                        </div>
                      )}
                      {outItems > 0 && (
                        <div className="text-center">
                          <p className="text-lg font-bold text-red-400">
                            {outItems}
                          </p>
                          <p className="text-[10px] text-gray-400 uppercase">
                            Out
                          </p>
                        </div>
                      )}
                      <div className="text-center">
                        <p className="text-lg font-bold text-green-400">
                          $
                          {totalValue.toLocaleString("en-CA", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </p>
                        <p className="text-[10px] text-gray-400 uppercase">
                          Value
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => setEditingWarehouse(wh)}
                          className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteWarehouse(wh)}
                          className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Items preview inside warehouse */}
                  {itemsInWh.length > 0 && (
                    <div className="border-t border-gray-600 bg-gray-800/40 px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        {itemsInWh.slice(0, 8).map((item) => {
                          const st = getStockStatus(item);
                          return (
                            <div
                              key={item._id}
                              className="flex items-center gap-1.5 px-2 py-1 bg-gray-700 rounded border border-gray-600 text-xs"
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${item.quantity <= 0 ? "bg-red-500" : item.quantity <= item.reorderPoint ? "bg-yellow-500" : "bg-green-500"}`}
                              />
                              <span className="font-mono text-gray-300">
                                {item.itemCode}
                              </span>
                              <span className="text-gray-400">
                                {item.quantity}
                              </span>
                              <span className="text-gray-600">{item.uom}</span>
                            </div>
                          );
                        })}
                        {itemsInWh.length > 8 && (
                          <span className="px-2 py-1 text-xs text-gray-500">
                            +{itemsInWh.length - 8} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
